import { createContext, useContext, useState, useEffect } from 'react'
import i18n from '../i18n'
import { supabase } from '../services/supabase'

const LanguageContext = createContext(null)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get from localStorage first (fastest)
    const saved = localStorage.getItem('i18nextLng')
    if (saved) {
      return saved
    }
    // Fallback to i18n's detected language
    try {
      return i18n.language || 'en'
    } catch {
      return 'en'
    }
  })
  const [isLoading, setIsLoading] = useState(true)

  // Load user's language preference from Supabase on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (user?.user_metadata?.preferred_language) {
          const userLang = user.user_metadata.preferred_language
          if (i18n.languages.includes(userLang)) {
            setCurrentLanguage(userLang)
            await i18n.changeLanguage(userLang)
            localStorage.setItem('i18nextLng', userLang)
          }
        }
      } catch (error) {
        console.error('Error loading user language preference:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUserLanguage()
  }, [i18n])

  // Sync current language with i18n
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage)
    }
  }, [currentLanguage, i18n])

  const changeLanguage = async (languageCode) => {
    try {
      // Update i18n
      await i18n.changeLanguage(languageCode)
      setCurrentLanguage(languageCode)
      
      // Update localStorage
      localStorage.setItem('i18nextLng', languageCode)
      
      // Update Supabase user_metadata if user is authenticated
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { error } = await supabase.auth.updateUser({
          data: {
            ...user.user_metadata,
            preferred_language: languageCode,
          },
        })
        
        if (error) {
          console.error('Error updating user language preference:', error)
          // Don't throw - localStorage is already updated, so it will persist
        }
      }
    } catch (error) {
      console.error('Error changing language:', error)
      throw error
    }
  }

  const availableLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
    { code: 'km', name: 'Khmer', nativeName: 'ភាសាខ្មែរ' },
  ]

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        changeLanguage,
        availableLanguages,
        isLoading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

